import { useCallback, useEffect, useRef, useState } from 'react';
import {
  carouselSlideAnimationDurationMs,
  carouselSlideAnimationNeedsExit,
  normalizeCarouselSlideAnimation,
  resolveCarouselNavDirection,
  type CarouselNavDirection,
} from './blockContentCodec';

/** Shared slide index + exit animation timing for carousels and product galleries. */
export function useCarouselIndexTransition(
  index: number,
  setIndex: (next: number) => void,
  animation: unknown,
  total: number,
) {
  const slideAnimation = normalizeCarouselSlideAnimation(animation);
  const needsExit = carouselSlideAnimationNeedsExit(slideAnimation);
  const exitDurationMs = carouselSlideAnimationDurationMs(slideAnimation);
  const [exiting, setExiting] = useState<number | null>(null);
  /** Bumps on each transition so CSS enter/exit keyframes reliably restart. */
  const [transitionGen, setTransitionGen] = useState(0);
  const [navDir, setNavDir] = useState<CarouselNavDirection>('forward');
  const exitTimerRef = useRef<number | null>(null);

  const beginExit = useCallback((fromIndex: number) => {
    if (!needsExit) return;
    if (exitTimerRef.current != null) window.clearTimeout(exitTimerRef.current);
    setExiting(fromIndex);
    const holdMs = Math.max(650, exitDurationMs + 50);
    exitTimerRef.current = window.setTimeout(() => {
      setExiting(null);
      exitTimerRef.current = null;
    }, holdMs);
  }, [exitDurationMs, needsExit]);

  const goTo = useCallback((next: number, forcedDir?: CarouselNavDirection) => {
    if (total <= 0) return;
    const target = ((next % total) + total) % total;
    if (target === index) return;
    setNavDir(forcedDir || resolveCarouselNavDirection(index, target, total));
    beginExit(index);
    setTransitionGen((g) => g + 1);
    setIndex(target);
  }, [beginExit, index, setIndex, total]);

  const goNext = useCallback(() => {
    if (total <= 0) return;
    goTo(index + 1, 'forward');
  }, [goTo, index, total]);

  const goPrev = useCallback(() => {
    if (total <= 0) return;
    goTo(index - 1, 'backward');
  }, [goTo, index, total]);

  useEffect(() => () => {
    if (exitTimerRef.current != null) window.clearTimeout(exitTimerRef.current);
  }, []);

  return { exiting, goTo, goNext, goPrev, slideAnimation, transitionGen, navDir };
}
